// ============================================================
// HOOKED — FISHING SYSTEM
// The catch-loop state machine. One gate, every phase:
//
//   CAST ──▶ WAIT ──▶ STRIKE ──▶ REEL ──▶ RESULT ──▶ (again)
//   (meter)  (bobber)  (ring)     (rhythm)  (card)
//
// All fish behavior comes from FishManifest (the one source of
// truth). All resource changes go through the angler reducer
// as deltas. This component owns ONLY the flow.
//
// v2 overhaul: LOGIC UNCHANGED. The HUD, pre-cast pill row, and
// wait scene wear The Treatment; the strike ring now receives
// the fish for its rising-shadow silhouette. Every phase
// transition, spend, and ATV/ACB branch is byte-identical.
//
// R-UNIVERSAL note: the fish's "choices" (refusing the chum,
// choosing the gauge number) funnel through the same resolve
// helpers a future PvP adversary would use.
// ============================================================

import { useCallback, useEffect, useRef, useState } from "react";
import { GM, SD, WHARF_SLAB, facet, bevelPanel, textGlow, ilkAura } from "./theme";
import { CastMeter } from "./CastMeter";
import { StrikeRing } from "./StrikeRing";
import { RhythmReel } from "./RhythmReel";
import { CatchCard, gradeCatch, rollWeight } from "./CatchCard";
import { rollFish, rollWaitPlan, depthForPower, getTome } from "./FishManifest";
import { FishArt } from "./FishArt";
import { SPENDS, FLOW_SPENDS } from "./angler";
import { murkBonus } from "./angler";
import { tackleStats, scalesForCatch, SCALES_FOR_CURIO } from "./tackle";

export function FishingOverlay({ angler, dispatch, fromBoat = false, onExit }) {
  const [phase, setPhase] = useState("CAST"); // CAST | THROW | WAIT | STRIKE | REEL | RESULT
  const [castInfo, setCastInfo] = useState(null);   // { power, depth, whipped }
  const [fish, setFish] = useState(null);
  const [strikeGrade, setStrikeGrade] = useState(null);
  const [result, setResult] = useState(null);
  const [nibbling, setNibbling] = useState(false);
  const [taken, setTaken] = useState(false);   // the true bite — bobber goes under
  const [gaugeReveal, setGaugeReveal] = useState(0);

  // Pre-cast spends (this cast only)
  const [mods, setMods] = useState({ trueStrike: false, moonwater: false, sweetened: false, eyes: false });
  // Tackle tomes (persist across casts this session)
  const [tomes, setTomes] = useState({ chum_slick: false, depth_gauge: false });

  // ── The Outfitter speaks — derived, never stored ────────────
  const tk = tackleStats(angler);

  const timersRef = useRef([]);
  const clearTimers = () => { timersRef.current.forEach(clearTimeout); timersRef.current = []; };
  useEffect(() => () => clearTimers(), []);

  // ── The cast beat: Khao refills, Lyf resets ─────────────────
  useEffect(() => {
    if (phase === "CAST") dispatch({ type: "NEW_CAST" });
  }, [phase, dispatch]);

  // ── Spend helpers (delta-authorized through the reducer) ────
  const spendAnima = useCallback((cost) => {
    if (angler.anima < cost) return false;
    dispatch({ type: "SPEND_ANIMA", cost });
    return true;
  }, [angler.anima, dispatch]);

  const spendFlow = useCallback((cost) => {
    if (angler.khao < cost) return false;
    dispatch({ type: "SPEND_FLOW", cost });
    return true;
  }, [angler.khao, dispatch]);

  const toggleMod = (key, spend) => {
    if (mods[key]) return; // spent is spent — no refunds mid-cast
    const ok = spend.pool === "anima" ? spendAnima(spend.cost) : spendFlow(spend.cost);
    if (ok) setMods(m => ({ ...m, [key]: true }));
  };

  // ── Phase 1 → THROW: lock the cast, let the line sail ───────
  const handleCast = useCallback(({ power, whipped }) => {
    // shore reach is the ROD's to decide (base 70 — THE DEEP needs a boat or better wood)
    const effPower = fromBoat ? power : Math.min(power, tk.shoreReach);
    const depth = depthForPower(effPower);
    setCastInfo({ power: effPower, depth, whipped });
    setPhase("THROW");   // the rod whips, the lure sails, then it plunks
  }, [fromBoat, tk.shoreReach]);

  // ── THROW → 2: the lure hits water — NOW the water answers ──
  const handleThrowLand = useCallback(() => {
    const depth = castInfo?.depth || depthForPower(0);
    const rolled = rollFish(depth, {
      sweetened: mods.sweetened,
      tierShift: tk.tierShift,
      ilkBias: tk.ilkBias,
    });

    if (rolled.curio) {
      // The Wizard's Cap surfaces on its own — no fight, only wonder.
      dispatch({ type: "GAIN_CURIO", curio: rolled });
      dispatch({ type: "GAIN_SCALES", amount: SCALES_FOR_CURIO });
      setResult({ kind: "CURIO", fish: rolled });
      setPhase("RESULT");
      return;
    }

    setFish(rolled);
    setPhase("WAIT");

    // ── Phase 2: the wait — nibbles lie, the true bite doesn't ──
    const chummed = tomes.chum_slick;
    const plan = rollWaitPlan({ chummed, waitMult: tk.waitMult });

    // ATV LAW — the fish may REFUSE the chum: instant bite,
    // faster ring, and the refusal destroys the slick.
    let refuse = false;
    if (chummed && Math.random() < getTome("chum_slick").refuseChance) {
      refuse = true;
      setTomes(t => ({ ...t, chum_slick: false }));
    }

    plan.nibbles.forEach(t => {
      if (refuse) return;
      timersRef.current.push(setTimeout(() => {
        setNibbling(true);
        timersRef.current.push(setTimeout(() => setNibbling(false), 650));
      }, t));
    });

    const biteAt = refuse ? 900 : plan.waitMs;
    timersRef.current.push(setTimeout(() => {
      setCastInfo(ci => ({ ...ci, refused: refuse }));
      // the TAKE plays inside WaitBobber; give it a beat, then strike
      setTaken(true);
      timersRef.current.push(setTimeout(() => {
        setTaken(false);
        setPhase("STRIKE");
      }, 520));
    }, biteAt));
  }, [castInfo, mods.sweetened, tomes.chum_slick, dispatch,
      tk.tierShift, tk.ilkBias, tk.waitMult]);

  // ── Phase 3 → 4: the strike resolves ────────────────────────
  const handleStrike = useCallback(({ grade }) => {
    if (grade === "MISS") {
      // Spooked — the fish keeps its freedom AND its memory of you.
      dispatch({ type: "FISH_ESCAPED", fishId: fish.id });
      setResult({ kind: "SPOOKED", fish: mods.eyes ? fish : null });
      setPhase("RESULT");
      return;
    }
    setStrikeGrade(grade);

    // ACB LAW — the fish chooses a number, aloud. It decides
    // what the Depth Gauge shows you.
    if (tomes.depth_gauge) {
      const [lo, hi] = getTome("depth_gauge").range;
      setGaugeReveal(lo + Math.floor(Math.random() * (hi - lo + 1)));
    } else {
      setGaugeReveal(0);
    }
    setPhase("REEL");
  }, [fish, mods.eyes, tomes.depth_gauge, dispatch]);

  // ── Phase 4 → 5: the fight resolves ─────────────────────────
  const handleReel = useCallback(({ outcome, accuracy }) => {
    if (outcome === "LANDED") {
      const grade = gradeCatch(strikeGrade, accuracy);
      const weight = rollWeight(fish, grade);
      dispatch({ type: "LAND_FISH", fish, grade, weight });   // grant-before-flag
      dispatch({ type: "GAIN_SCALES", amount: scalesForCatch(fish, grade) }); // its own lane
      dispatch({ type: "RESTORE_ANIMA", amount: 2 });
      setResult({ kind: "LANDED", fish, grade, weight });
    } else if (outcome === "PROPHECY") {
      // The Inversion: she escaped — she won — so you win.
      dispatch({ type: "LAND_FISH", fish, grade: "PROPHECY", weight: null });
      dispatch({ type: "GAIN_SCALES", amount: scalesForCatch(fish, "PROPHECY") });
      setResult({ kind: "PROPHECY", fish, grade: "FLAWLESS" });
    } else if (outcome === "SNAPPED") {
      dispatch({ type: "FISH_ESCAPED", fishId: fish.id });
      dispatch({ type: "DIM_ANIMA", amount: 4 });             // the recoil dims you
      setResult({ kind: "SNAPPED", fish });
    } else {
      dispatch({ type: "FISH_ESCAPED", fishId: fish.id });
      setResult({ kind: "ESCAPED", fish });
    }
    setPhase("RESULT");
  }, [fish, strikeGrade, dispatch]);

  // ── Phase 5 → 1: back to the water ──────────────────────────
  const handleCloseCard = useCallback(() => {
    clearTimers();
    setFish(null); setCastInfo(null); setStrikeGrade(null);
    setResult(null); setNibbling(false); setTaken(false); setGaugeReveal(0);
    setMods({ trueStrike: false, moonwater: false, sweetened: false, eyes: false });
    setPhase("CAST");
  }, []);

  // Exit with Escape during CAST only (mid-fight you're committed)
  useEffect(() => {
    if (phase !== "CAST") return;
    const onKey = (e) => { if (e.key === "Escape") { e.preventDefault(); onExit?.(); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [phase, onExit]);

  const windowMult = fish ? murkBonus(angler, fish.id) * (mods.trueStrike ? 2 : 1) : 1;

  return (
    <>
      {/* Resource HUD — always visible while fishing */}
      <ResourceHUD angler={angler} depth={castInfo?.depth} />

      {phase === "CAST" && (
        <>
          <PreCastRow
            angler={angler} mods={mods} tomes={tomes}
            onMod={toggleMod}
            onTome={(id) => setTomes(t => ({ ...t, [id]: !t[id] }))}
          />
          <CastMeter onCast={handleCast} />
        </>
      )}

      {phase === "THROW" && (
        <CastThrow
          depth={castInfo?.depth}
          whipped={castInfo?.whipped}
          bobColor={tk.bobberColor}
          lineColor={tk.lineColor}
          fromBoat={fromBoat}
          onLand={handleThrowLand}
        />
      )}

      {phase === "WAIT" && (
        <WaitBobber nibbling={nibbling} taken={taken} eyes={mods.eyes} fish={fish}
          bobGlyph={tk.bobberGlyph} bobColor={tk.bobberColor} lineColor={tk.lineColor} />
      )}

      {phase === "STRIKE" && fish && (
        <StrikeRing
          ringMs={fish.ringMs * (castInfo?.refused ? getTome("chum_slick").ringPenalty : 1)}
          slowFactor={(mods.moonwater ? 1.4 : 1) * tk.ringSlow}
          windowMult={windowMult}
          whipped={castInfo?.whipped}
          fishColor={fish.color}
          fish={fish}
          onResolve={handleStrike}
        />
      )}

      {phase === "REEL" && fish && (
        <RhythmReel
          fish={fish}
          angler={{ ...angler, lyt: angler.lyt + tk.lyt, lyf: angler.lyf + tk.lyf }}
          strikeGrade={strikeGrade}
          windowMult={murkBonus(angler, fish.id)}
          gaugeReveal={gaugeReveal}
          onSpendAnima={spendAnima}
          onResolve={handleReel}
        />
      )}

      {phase === "RESULT" && result && (
        <CatchCard result={result} onClose={handleCloseCard} />
      )}
    </>
  );
}

// ── Resource HUD ─────────────────────────────────────────────
function ResourceHUD({ angler, depth }) {
  return (
    <div style={{
      position: "fixed", top: 14, left: "50%", transform: "translateX(-50%)",
      zIndex: 310, display: "flex", gap: 16, alignItems: "center",
      background: WHARF_SLAB, clipPath: facet(10),
      padding: "9px 20px",
      ...bevelPanel(GM.gold),
      fontFamily: GM.fontUI, fontSize: 11,
    }}>
      <Stat label="GRIT" value={`${angler.anima}/${angler.maxAnima}`} color={GM.goldBright} glyph="✦" />
      <Divider />
      <Stat label="FLOW" value={`${angler.khao}/${angler.maxKhao}`} color={SD.tealLight} glyph="✧" />
      <Divider />
      <Stat label="REPUTE" value={angler.renown} color={GM.silver} glyph="◆" />
      <Divider />
      <Stat label="SCALES" value={angler.scales ?? 0} color={GM.goldBright} glyph="❖" />
      {depth && (
        <>
          <Divider />
          <div style={{ fontSize: 9, letterSpacing: 2, color: SD.starDim, ...textGlow(SD.teal) }}>{depth.label}</div>
        </>
      )}
    </div>
  );
}

function Divider() {
  return <div style={{ width: 1, height: 16, background: `linear-gradient(180deg, transparent, ${GM.inkBrown}, transparent)` }} />;
}

function Stat({ label, value, color, glyph }) {
  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: 5 }}>
      <span style={{ color, fontSize: 12, textShadow: `0 0 8px ${color}88` }}>{glyph}</span>
      <span style={{ color: GM.inkDim, fontSize: 8, letterSpacing: 1.5 }}>{label}</span>
      <span style={{ color: GM.ink, fontWeight: 900 }}>{value}</span>
    </div>
  );
}

// ── Pre-cast spend row ───────────────────────────────────────
function PreCastRow({ angler, mods, tomes, onMod, onTome }) {
  const pill = (active, affordable) => ({
    padding: "6px 11px", borderRadius: 999, fontSize: 9, letterSpacing: 1,
    fontFamily: GM.fontUI, cursor: affordable || active ? "pointer" : "default",
    userSelect: "none",
    border: `1px solid ${active ? GM.goldBright : affordable ? SD.purpleLight + "88" : GM.inkBrown}`,
    color: active ? GM.pageDeep : affordable ? SD.purpleLight : GM.inkDim,
    opacity: affordable || active ? 1 : 0.45,
    background: active
      ? `linear-gradient(180deg, ${GM.goldPale}, ${GM.gold})`
      : affordable ? `${SD.purple}18` : "transparent",
    boxShadow: active
      ? `0 2px 8px ${GM.gold}55, inset 0 1px 0 #fff8`
      : affordable ? `0 0 8px ${SD.purple}22` : "none",
    transition: "all 0.12s",
  });
  return (
    <div style={{
      position: "fixed", left: "50%", bottom: 224, transform: "translateX(-50%)",
      zIndex: 300, display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "center",
      width: "min(560px, 94vw)",
    }}>
      <div style={pill(mods.trueStrike, angler.anima >= SPENDS.TRUE_STRIKE.cost)}
           onPointerDown={() => onMod("trueStrike", { pool: "anima", cost: SPENDS.TRUE_STRIKE.cost })}>
        TRUE STRIKE {SPENDS.TRUE_STRIKE.cost}✦
      </div>
      <div style={pill(mods.moonwater, angler.khao >= FLOW_SPENDS.MOONWATER.cost)}
           onPointerDown={() => onMod("moonwater", { pool: "khao", cost: FLOW_SPENDS.MOONWATER.cost })}>
        MOONWATER {FLOW_SPENDS.MOONWATER.cost}✧
      </div>
      <div style={pill(mods.sweetened, angler.khao >= FLOW_SPENDS.SWEETEN.cost)}
           onPointerDown={() => onMod("sweetened", { pool: "khao", cost: FLOW_SPENDS.SWEETEN.cost })}>
        SWEETEN {FLOW_SPENDS.SWEETEN.cost}✧
      </div>
      <div style={pill(mods.eyes, angler.khao >= FLOW_SPENDS.EYES_BELOW.cost)}
           onPointerDown={() => onMod("eyes", { pool: "khao", cost: FLOW_SPENDS.EYES_BELOW.cost })}>
        EYES BELOW {FLOW_SPENDS.EYES_BELOW.cost}✧
      </div>
      <div style={pill(tomes.chum_slick, true)} onPointerDown={() => onTome("chum_slick")}>
        🪣 CHUM SLICK · ATV
      </div>
      <div style={pill(tomes.depth_gauge, true)} onPointerDown={() => onTome("depth_gauge")}>
        🧭 DEPTH GAUGE · ACB
      </div>
    </div>
  );
}

// ── THE CAST THROW — the line sails, the lure plunks ─────────
// Plays once between locking the meter and the bobber landing.
// The rod whips forward, a lure arcs out over the water on a
// depth-scaled trajectory, and hits with impact rings + a splash.
// onLand fires at the plunk so the wait picks up exactly where
// the lure struck. Pure feel — no logic lives here.
function CastThrow({ depth, whipped, bobColor = SD.gold, lineColor = SD.foam, fromBoat, onLand }) {
  const landedRef = useRef(false);
  // depth decides distance: shallows near, drift mid, deep far.
  const reach = depth?.id === "deep" ? 1 : depth?.id === "mid" ? 0.66 : 0.4;
  // sail vector (px), from the caster (lower center) up over the water
  const sailX = 0;                                  // straight ahead (up-screen)
  const sailY = -(120 + reach * 210);               // higher = further out
  const sailMs = 620 - (whipped ? 90 : 0);          // whip = snappier

  useEffect(() => {
    const land = setTimeout(() => {
      if (landedRef.current) return;
      landedRef.current = true;
      onLand?.();
    }, sailMs + 260);   // sail + a beat for the plunk to read
    return () => clearTimeout(land);
  }, [sailMs, onLand]);

  // the lure's resting spot (where it plunks) — center, up-screen
  const plunkTop = `calc(50% + ${sailY}px)`;

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 305, pointerEvents: "none",
      overflow: "hidden",
    }}>
      {/* the caster's rod, lower center — whips forward on release */}
      <div style={{
        position: "absolute", left: "50%", bottom: 70,
        transform: "translateX(-50%)", transformOrigin: "bottom center",
        animation: `castRelease ${sailMs}ms cubic-bezier(.2,.8,.3,1) both`,
      }}>
        <div style={{
          width: 4, height: 84,
          background: `linear-gradient(180deg, ${GM.ropeLight}, ${GM.rope} 70%, ${GM.ropeDim})`,
          borderRadius: 3, transformOrigin: "bottom center",
          boxShadow: "0 2px 4px rgba(0,0,0,0.5)",
        }} />
      </div>

      {/* the line — an arc from rod tip out to the lure, drawing as
          it flies. Normalized 0..100 viewBox stretched to fill, so
          no viewport-pixel math is needed (clientWidth law honored). */}
      {(() => {
        const tipY = 92;                          // rod tip, % down
        const landY = 50 + (sailY / (120 + 210)) * 42;  // map sailY→% (deeper=higher)
        const ctrlY = (tipY + landY) / 2;
        return (
          <svg viewBox="0 0 100 100" preserveAspectRatio="none"
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}>
            <path
              d={`M 50 ${tipY} Q 46 ${ctrlY} 50 ${landY}`}
              fill="none" stroke={lineColor} strokeWidth="0.4" strokeOpacity="0.8"
              strokeDasharray="120"
              style={{ animation: `lineWhip ${sailMs}ms ease-out both` }}
              vectorEffect="non-scaling-stroke"
            />
          </svg>
        );
      })()}

      {/* the lure/bobber sailing out along the arc */}
      <div style={{
        position: "absolute", left: "50%", bottom: 150,
        transform: "translateX(-50%)",
        "--sailX": `${sailX}px`, "--sailY": `${sailY}px`,
        animation: `lureSail ${sailMs}ms cubic-bezier(.3,.5,.4,1) both`,
        fontSize: 20, filter: `drop-shadow(0 0 8px ${bobColor}88)`,
      }}>
        <div style={{
          width: 12, height: 12, borderRadius: "50%",
          background: `radial-gradient(circle at 35% 30%, #fff, ${bobColor} 60%, ${SD.waterDeep})`,
          boxShadow: `0 0 8px ${bobColor}aa`,
        }} />
      </div>

      {/* THE PLUNK — impact rings + splash where the lure lands */}
      <div style={{
        position: "absolute", left: "50%", top: plunkTop,
        transform: "translate(-50%,-50%)",
      }}>
        {[0, 1, 2].map(k => (
          <div key={k} style={{
            position: "absolute", left: "50%", top: "50%",
            width: 46, height: 20,
            border: `1.5px solid ${SD.foam}${k === 0 ? "aa" : "55"}`, borderRadius: "50%",
            animation: `plunkRing 0.8s ease-out ${sailMs + k * 90}ms both`,
          }} />
        ))}
        {/* splash droplets */}
        {[-1, 0, 1].map(k => (
          <div key={`s${k}`} style={{
            position: "absolute", left: "50%", top: "50%",
            width: 3, height: 10, borderRadius: 2, background: SD.foam,
            transform: `translate(-50%,-50%) rotate(${k * 26}deg)`,
            animation: `plunkSplash 0.5s ease-out ${sailMs + 40}ms both`,
            transformOrigin: "bottom center",
          }} />
        ))}
      </div>
    </div>
  );
}


function WaitBobber({ nibbling, taken, eyes, fish, bobGlyph = "🎈", bobColor = SD.gold, lineColor = SD.foam }) {
  const aura = fish ? ilkAura(fish.ilk) : SD.foam;
  const bobAnim = taken
    ? "bobberTake 0.5s cubic-bezier(.5,0,.8,1) forwards"
    : nibbling ? "bobberNibble 0.6s ease" : "bobberIdle 2.4s ease-in-out infinite";
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 300,
      display: "flex", alignItems: "center", justifyContent: "center",
      pointerEvents: "none",
    }}>
      <div style={{ position: "relative", textAlign: "center" }}>
        {/* the line running up out of frame to the rod */}
        <div style={{
          position: "absolute", left: "50%", bottom: "42%", width: 1, height: 120,
          transform: "translateX(-50%)",
          background: `linear-gradient(180deg, transparent, ${lineColor}66)`,
          opacity: taken ? 1 : 0.5,
          animation: taken ? "lineTaut 0.3s ease-in-out infinite" : "none",
        }} />

        {/* concentric standing ripples on the water */}
        {[0, 1, 2].map(k => (
          <div key={k} style={{
            position: "absolute", left: "50%", top: "58%",
            width: 50 + k * 34, height: 18 + k * 12, transform: "translate(-50%,-50%)",
            border: `1px solid ${SD.ripple}${k === 0 ? "55" : "22"}`, borderRadius: "50%",
            animation: `waterBreathe ${3 + k}s ease-in-out ${k * 0.4}s infinite`,
          }} />
        ))}

        {/* ripple ring burst on nibble */}
        {nibbling && !taken && (
          <div style={{
            position: "absolute", left: "50%", top: "58%",
            width: 60, height: 24, transform: "translate(-50%,-50%)",
            border: `1.5px solid ${SD.foam}88`, borderRadius: "50%",
            animation: "rippleOut 0.7s ease-out forwards",
          }} />
        )}

        {/* THE TAKE — a dark shape surges up beneath the bobber */}
        {taken && (
          <>
            <div style={{
              position: "absolute", left: "50%", top: "62%",
              width: 70, height: 30, transform: "translate(-50%,-50%)", borderRadius: "50%",
              background: `radial-gradient(ellipse, ${SD.waterDeep}, transparent 70%)`,
              animation: "takeShadow 0.5s ease-out forwards",
            }} />
            {[0, 1].map(k => (
              <div key={k} style={{
                position: "absolute", left: "50%", top: "60%",
                width: 54, height: 22, transform: "translate(-50%,-50%)",
                border: `2px solid ${SD.foam}`, borderRadius: "50%",
                animation: `plunkRing 0.55s ease-out ${k * 0.1}s forwards`,
              }} />
            ))}
          </>
        )}

        <div style={{
          fontSize: 32,
          animation: bobAnim,
          filter: `drop-shadow(0 4px 8px rgba(0,0,0,0.5)) drop-shadow(0 0 8px ${bobColor}88)`,
        }}>{bobGlyph}</div>

        <div style={{
          marginTop: 16, fontFamily: GM.fontDisplay,
          fontSize: taken ? 14 : 10, fontWeight: taken ? 900 : 400,
          letterSpacing: taken ? 5 : 3,
          color: taken ? GM.goldBright : nibbling ? SD.foam : SD.starDim,
          textShadow: taken ? `0 0 12px ${GM.gold}` : "none",
          animation: taken ? "beatPop 0.3s ease" : "moonShimmer 3s ease-in-out infinite",
          transition: "color 0.1s",
        }}>
          {taken ? "STRIKE!" : nibbling ? "…a lie…" : "THE WATER WAITS"}
        </div>

        {eyes && fish && !taken && (
          <div style={{ marginTop: 12, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
            <div style={{ opacity: 0.7, filter: "blur(0.5px)" }}>
              <FishArt fish={fish} size={54} animate />
            </div>
            <div style={{
              fontFamily: GM.fontUI, fontSize: 10,
              letterSpacing: 1.5, color: aura, ...textGlow(aura),
            }}>
              {fish.name} circles below
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
